import hl7FieldKeyMapRaw from '../../../assets/rule-config/field-key-map-hl7.json';
import {
  buildHl7ObxParentIdPath,
  buildHl7ObrRepeatPath,
  collapseHl7SlashPredicateAttributeToFlatKey,
  expandHl7ComponentKeyToSlashNotation,
} from './hl7-field-key-map.flatten';
import { normalizeXmlFieldPersistPath } from '../xml/xml-field-key-map.flatten';
import type { PathBuildElementStep, PathBuildModel, PathFilterRow } from '../xml/xml-path-step-builder';
import {
  coalesceDataType,
  formatXmlAttributePredicate,
  getRepeatableStepIndices,
  parsePredicateElementSegment,
} from '../xml/xml-path-step-builder';

interface Hl7MapNode {
  name: string;
  label?: string;
  type?: string;
  xpath?: string;
  isRepeatable?: boolean;
  attributes?: Hl7MapNode[];
  children?: Hl7MapNode[];
}

/** Type guard for the HL7 field-key-map JSON root (`HL7Message` + xpath). @example isHl7Root(hl7FieldKeyMapRaw) */
function isHl7Root(n: unknown): n is Hl7MapNode {
  if (!n || typeof n !== 'object') {
    return false;
  }
  const d = n as Hl7MapNode;
  return d.name === 'HL7Message' && typeof d.xpath === 'string';
}

/** Strips HL7 path predicates and indices from a segment token. @example bareSegmentName('OBR[OBR.4=1][2]') // 'OBR' */
function bareSegmentName(seg: string): string {
  if (!seg || seg.startsWith('@')) {
    return '';
  }
  return seg.split('[')[0].split('[@')[0].trim();
}

/** Maps each HL7 tree node to its parent (root maps to null). @example buildParentChildMap(doc).get(obxNode)?.name */
function buildParentChildMap(root: Hl7MapNode): Map<Hl7MapNode, Hl7MapNode | null> {
  const m = new Map<Hl7MapNode, Hl7MapNode | null>();
  m.set(root, null);
  const q: Hl7MapNode[] = [root];
  while (q.length) {
    const n = q.shift()!;
    for (const c of n.children ?? []) {
      m.set(c, n);
      q.push(c);
    }
  }
  return m;
}

/** Find segment + leaf attribute whose xpath equals the template path. */
function findLeafByFullXpath(
  root: Hl7MapNode,
  target: string
): { segment: Hl7MapNode; leafAttr: Hl7MapNode } | null {
  const norm = normalizeXmlFieldPersistPath(target);
  const visit = (n: Hl7MapNode): { segment: Hl7MapNode; leafAttr: Hl7MapNode } | null => {
    for (const a of n.attributes ?? []) {
      const ax = (a.xpath || '').trim();
      if (ax && normalizeXmlFieldPersistPath(ax) === norm) {
        return { segment: n, leafAttr: a };
      }
    }
    for (const c of n.children ?? []) {
      const cx = (c.xpath || '').trim();
      if (cx && normalizeXmlFieldPersistPath(cx) === norm) {
        return { segment: n, leafAttr: c };
      }
      const hit = visit(c);
      if (hit) {
        return hit;
      }
    }
    return null;
  };
  return visit(root);
}

/**
 * Builds a PathBuildModel from a canonical HL7 template xpath for the Build Path dialog.
 * @example buildHl7PathModelFromTemplatePath('HL7Message/OBR/OBX/OBX.5/OBX.5.1')?.model.steps.length
 */
export function buildHl7PathModelFromTemplatePath(
  templatePath: string
): { model: PathBuildModel; serializeTemplate: string } | null {
  if (!isHl7Root(hl7FieldKeyMapRaw)) {
    return null;
  }
  const doc = hl7FieldKeyMapRaw as Hl7MapNode;
  const tp = (templatePath || '').trim();
  if (!tp) {
    return null;
  }
  const found = findLeafByFullXpath(doc, tp);
  if (!found) {
    return null;
  }
  const parents = buildParentChildMap(doc);
  const chain: Hl7MapNode[] = [];
  let cur: Hl7MapNode | null = found.segment;
  while (cur) {
    chain.unshift(cur);
    cur = parents.get(cur) ?? null;
  }
  const steps: PathBuildElementStep[] = chain
    .filter((n) => !!n.isRepeatable)
    .map((n) => ({
      elementName: (n.name || '').trim(),
      isRepeatable: true,
      attributeOptions: (n.attributes ?? []).map((a) => ({
        name: (a.name || '').trim(),
        label: ((a.label || a.name) || '').trim(),
        dataType: undefined as string | undefined,
      })),
      filters: [],
      occurrenceIndex: 1,
    }));
  if (!steps.length) {
    return null;
  }
  const leafName = (found.leafAttr.name || '').trim();
  const leafAttributeOptions = (found.segment.attributes ?? []).map((a) => ({
    name: (a.name || '').trim(),
    label: ((a.label || a.name) || '').trim(),
    dataType: undefined as string | undefined,
  }));
  const model: PathBuildModel = {
    steps,
    repeatableStepIndices: [],
    leafAttribute: leafName,
    leafAttributeOptions,
  };
  model.repeatableStepIndices = getRepeatableStepIndices(model);
  if (!model.repeatableStepIndices.length) {
    return null;
  }
  return { model, serializeTemplate: normalizeXmlFieldPersistPath(tp) };
}

/** Depth-first lookup of a segment node by name (case-insensitive). @example findHl7SegmentNode(doc, 'OBX')?.isRepeatable */
function findHl7SegmentNode(root: Hl7MapNode, segmentName: string): Hl7MapNode | null {
  const target = (segmentName || '').trim().toUpperCase();
  if (!target) {
    return null;
  }
  const stack: Hl7MapNode[] = [root];
  while (stack.length) {
    const n = stack.shift()!;
    if ((n.name || '').trim().toUpperCase() === target) {
      return n;
    }
    for (const c of n.children ?? []) {
      stack.push(c);
    }
  }
  return null;
}

/** Converts an HL7 map segment into a repeatable Build Path step with attribute options. @example stepFromHl7Node(obrNode).elementName // 'OBR' */
function stepFromHl7Node(n: Hl7MapNode): PathBuildElementStep {
  return {
    elementName: (n.name || '').trim(),
    displayLabel: ((n.label || n.name) || '').trim(),
    isRepeatable: true,
    attributeOptions: (n.attributes ?? []).map((a) => ({
      name: (a.name || '').trim(),
      label: ((a.label || a.name) || '').trim(),
      dataType: undefined as string | undefined,
    })),
    filters: [],
    occurrenceIndex: 1,
  };
}

/** Repeatable descendants of a segment (e.g. OBX under OBR). */
function collectRepeatableDescendants(node: Hl7MapNode): Hl7MapNode[] {
  const out: Hl7MapNode[] = [];
  const queue: Hl7MapNode[] = [...(node.children ?? [])];
  while (queue.length) {
    const n = queue.shift()!;
    if (n.isRepeatable) {
      out.push(n);
    }
    for (const c of n.children ?? []) {
      queue.push(c);
    }
  }
  return out;
}

/** Builds PathBuildModel for a target segment, ancestors, and repeatable descendants. @example buildHl7PathModelFromSegmentNode(obx, doc, 'OBX.5.1', 'HL7Message/OBR/OBX')?.model.steps.length */
function buildHl7PathModelFromSegmentNode(
  segment: Hl7MapNode,
  root: Hl7MapNode,
  leafAttribute: string,
  serializeTemplate: string
): { model: PathBuildModel; serializeTemplate: string } | null {
  const parents = buildParentChildMap(root);
  const chain: Hl7MapNode[] = [];
  let cur: Hl7MapNode | null = segment;
  while (cur) {
    chain.unshift(cur);
    cur = parents.get(cur) ?? null;
  }
  const steps: PathBuildElementStep[] = chain.filter((n) => !!n.isRepeatable).map((n) => stepFromHl7Node(n));
  const stepNames = new Set(steps.map((s) => s.elementName.toUpperCase()));
  for (const child of collectRepeatableDescendants(segment)) {
    const name = (child.name || '').trim().toUpperCase();
    if (name && !stepNames.has(name)) {
      steps.push(stepFromHl7Node(child));
      stepNames.add(name);
    }
  }
  if (!steps.length) {
    return null;
  }
  const leaf = (leafAttribute || '').trim();
  const leafParts = /^([A-Za-z0-9]{2,4})\.(\d+)(?:\.(\d+))?$/.exec(leaf);
  const model: PathBuildModel = {
    steps,
    repeatableStepIndices: [],
    leafAttribute: leaf,
    leafAttributeOptions: [],
  };
  model.repeatableStepIndices = getRepeatableStepIndices(model);
  if (!model.repeatableStepIndices.length) {
    return null;
  }
  if (leafParts) {
    const leafSeg = leafParts[1].toUpperCase();
    for (const step of model.steps) {
      if (step.isRepeatable && step.elementName.toUpperCase() === leafSeg) {
        step.hl7FieldIndex = leafParts[2];
        step.hl7ComponentIndex = (leafParts[3] ?? '').trim();
      }
    }
  }
  return { model, serializeTemplate: normalizeXmlFieldPersistPath(serializeTemplate) };
}

/**
 * Builds path model for a target HL7 segment including parent and child repeatables (e.g. OBR + OBX).
 * @example buildHl7PathModelForTargetSegment('OBX', 'OBX.5.1', 'HL7Message/OBR/OBX')?.model
 */
export function buildHl7PathModelForTargetSegment(
  targetSegment: string,
  displayKey: string,
  templatePath: string
): { model: PathBuildModel; serializeTemplate: string } | null {
  const target = (targetSegment || '').trim().toUpperCase();
  const dk = (displayKey || '').trim() || target;
  const tp = (templatePath || '').trim();
  if (!target || !tp) {
    return null;
  }
  if (!isHl7Root(hl7FieldKeyMapRaw)) {
    return null;
  }
  const doc = hl7FieldKeyMapRaw as Hl7MapNode;
  const segment = findHl7SegmentNode(doc, target);
  if (!segment?.isRepeatable) {
    return null;
  }
  const fromKey = /^([A-Za-z0-9]{2,4})\.(\d+)/.test(dk) ? dk : `${target}.1`;
  return buildHl7PathModelFromSegmentNode(segment, doc, fromKey, tp);
}

/**
 * Builds HL7 display key and persisted slash path from segment picker indices.
 * @example buildHl7PathFromSegmentPicker({ segment: 'MSH', fieldIndex: '12', componentIndex: '1', segmentBasePath: 'HL7Message/MSH' })
 */
export function buildHl7PathFromSegmentPicker(args: {
  segment: string;
  fieldIndex: string | number;
  componentIndex?: string | number | null;
  subComponentIndex?: string | number | null;
  segmentBasePath: string;
}): { displayKey: string; path: string } {
  const seg = (args.segment || '').trim().toUpperCase();
  const fieldIdx = `${args.fieldIndex ?? ''}`.trim();
  const compIdx = `${args.componentIndex ?? ''}`.trim();
  const subIdx = `${args.subComponentIndex ?? ''}`.trim();
  if (!seg || !fieldIdx) {
    return { displayKey: '', path: '' };
  }
  const numericParts = [fieldIdx];
  if (compIdx) {
    numericParts.push(compIdx);
  }
  if (subIdx && compIdx) {
    numericParts.push(subIdx);
  }
  const displayKey = [seg, ...numericParts].join('.');
  const base = normalizeXmlFieldPersistPath(
    (args.segmentBasePath || '').trim() || `HL7Message/${seg}`
  );
  let running = seg;
  const tail = numericParts.map((idx) => {
    running = `${running}.${idx}`;
    return running;
  });
  return { displayKey, path: normalizeXmlFieldPersistPath(`${base}/${tail.join('/')}`) };
}

/** Deepest flat HL7 key token for a segment within slash path body parts. */
function deepestHl7FlatKeyForSegment(segmentName: string, parts: string[], startIdx: number): string {
  const seg = (segmentName || '').trim().toUpperCase();
  if (!seg || startIdx < 0 || startIdx >= parts.length) {
    return '';
  }
  let deepest = '';
  for (let i = startIdx; i < parts.length; i++) {
    const flat = collapseHl7SlashPredicateAttributeToFlatKey((parts[i] || '').trim());
    if (new RegExp(`^${seg}\\.\\d+`, 'i').test(flat)) {
      deepest = flat;
      continue;
    }
    if (bareSegmentName(parts[i]) !== seg) {
      break;
    }
  }
  return deepest;
}

/** Parses field/component/sub-component indices after a segment in a slash HL7 path. */
function parseHl7IndicesAfterSegment(
  segmentName: string,
  parts: string[],
  startIdx: number
): { fieldIndex: string; componentIndex: string; subComponentIndex: string } | null {
  const seg = (segmentName || '').trim().toUpperCase();
  const deepest = deepestHl7FlatKeyForSegment(seg, parts, startIdx);
  if (!deepest) {
    return null;
  }
  const parsed = parseHl7ComponentKeyParts(seg, deepest);
  if (parsed.fieldIndex == null) {
    return null;
  }
  return {
    fieldIndex: `${parsed.fieldIndex}`,
    componentIndex: parsed.componentIndex != null ? `${parsed.componentIndex}` : '',
    subComponentIndex: parsed.subComponentIndex != null ? `${parsed.subComponentIndex}` : '',
  };
}

/** Builds flat display key (e.g. OBX.2.2.3) from segment and index strings. */
function hl7FlatDisplayKeyFromIndices(
  segment: string,
  fieldIndex: string,
  componentIndex?: string | null,
  subComponentIndex?: string | null
): string {
  return buildHl7PathFromSegmentPicker({
    segment,
    fieldIndex,
    componentIndex: componentIndex || null,
    subComponentIndex: subComponentIndex || null,
    segmentBasePath: `HL7Message/${segment}`,
  }).displayKey;
}

/**
 * Applies a simple HL7 slash persisted path into per-repeatable-step field/component indices.
 * @example applyHl7ComponentPathToModel(model, 'HL7Message/MSH/MSH.12/MSH.12.1'); model.steps[0].hl7FieldIndex
 */
export function applyHl7ComponentPathToModel(model: PathBuildModel, persistedPath: string): void {
  const parts = normalizeXmlFieldPersistPath(persistedPath).split('/').filter(Boolean);
  if (parts[0]?.toUpperCase() !== 'HL7MESSAGE') {
    return;
  }
  const body = parts.slice(1);
  for (const step of model.steps) {
    if (!step.isRepeatable) {
      continue;
    }
    const seg = step.elementName;
    const i = body.findIndex((p) => bareSegmentName(p) === seg);
    if (i < 0) {
      step.hl7FieldIndex = '';
      step.hl7ComponentIndex = '';
      continue;
    }
    const parsed = parseHl7IndicesAfterSegment(seg, body, i + 1);
    step.hl7FieldIndex = parsed?.fieldIndex ?? '';
    step.hl7ComponentIndex = parsed?.componentIndex ?? '';
  }
  const leafSeg = (model.leafAttribute || '').trim().split('.')[0].toUpperCase();
  const leafStep = model.steps.filter((s) => s.isRepeatable).pop();
  const target = leafStep?.elementName.toUpperCase() || leafSeg;
  if (target && model.steps.some((s) => s.elementName.toUpperCase() === target)) {
    const segIdx = body.findIndex((p) => bareSegmentName(p) === target);
    const deepest =
      segIdx >= 0 ? deepestHl7FlatKeyForSegment(target, body, segIdx + 1) : '';
    if (deepest) {
      model.leafAttribute = deepest;
      return;
    }
    const last = [...model.steps].reverse().find((s) => s.elementName.toUpperCase() === target);
    if (last?.hl7FieldIndex) {
      model.leafAttribute = hl7FlatDisplayKeyFromIndices(
        target,
        last.hl7FieldIndex,
        last.hl7ComponentIndex ?? ''
      );
    }
  }
}

/**
 * Parses flat or slash HL7 component key into field and subcomponent indices for a segment.
 * @example parseHl7ComponentKeyParts('OBX', 'OBX.5.1.2') // { fieldIndex: 5, componentIndex: 1, subComponentIndex: 2 }
 */
export function parseHl7ComponentKeyParts(
  segment: string,
  keyOrAttr: string
): { fieldIndex: number | null; componentIndex: number | null; subComponentIndex: number | null } {
  const seg = (segment || '').trim().toUpperCase();
  const flat = collapseHl7SlashPredicateAttributeToFlatKey((keyOrAttr || '').trim());
  if (!seg || !flat) {
    return { fieldIndex: null, componentIndex: null, subComponentIndex: null };
  }
  const m = new RegExp(`^${seg}\\.(\\d+)(?:\\.(\\d+))?(?:\\.(\\d+))?$`, 'i').exec(flat);
  if (m) {
    return {
      fieldIndex: Number(m[1]),
      componentIndex: m[2] ? Number(m[2]) : null,
      subComponentIndex: m[3] ? Number(m[3]) : null,
    };
  }
  return { fieldIndex: null, componentIndex: null, subComponentIndex: null };
}

/** Sets hl7FieldIndex/hl7ComponentIndex on a step from a persisted leaf tail path. @example applyHl7LeafTailToStep(obxStep, 'OBX.5/OBX.5.1'); obxStep.hl7FieldIndex */
function applyHl7LeafTailToStep(step: PathBuildElementStep, leafTail: string): void {
  const seg = (step.elementName || '').trim().toUpperCase();
  const tail = (leafTail || '').trim().replace(/^\/+/, '');
  if (!seg || !tail) {
    return;
  }
  const last = tail.includes('/') ? tail.split('/').pop()! : tail;
  const flat = collapseHl7SlashPredicateAttributeToFlatKey(last);
  const parts = parseHl7ComponentKeyParts(seg, flat);
  if (parts.fieldIndex != null) {
    step.hl7FieldIndex = `${parts.fieldIndex}`;
    step.hl7ComponentIndex =
      parts.componentIndex != null ? `${parts.componentIndex}` : '';
  }
}

/** Serializes per-step field/component picks to slash leaf tail notation. @example hl7LeafTailFromStep({ elementName: 'OBX', hl7FieldIndex: '5', hl7ComponentIndex: '1', ... }) */
function hl7LeafTailFromStep(
  step: PathBuildElementStep,
  subComponentIndex?: string | null
): string {
  const seg = (step.elementName || '').trim();
  const fi = (step.hl7FieldIndex ?? '').trim();
  const ci = (step.hl7ComponentIndex ?? '').trim();
  const sci = (subComponentIndex ?? '').trim();
  if (!seg || !fi) {
    return '';
  }
  const key = hl7FlatDisplayKeyFromIndices(seg, fi, ci || null, sci || null);
  return expandHl7ComponentKeyToSlashNotation(key);
}

/** Map filter rows to OBR/OBX predicate components; skips empty values. */
function hl7FiltersToComponents(step: PathBuildElementStep): { name: string; value: string }[] {
  const seg = (step.elementName || '').trim().toUpperCase();
  const out: { name: string; value: string }[] = [];
  for (const f of step.filters ?? []) {
    const val = `${f.value ?? ''}`.trim();
    if (!val) {
      continue;
    }
    let name = collapseHl7SlashPredicateAttributeToFlatKey((f.attribute ?? '').trim());
    if (!name) {
      continue;
    }
    if (!new RegExp(`^${seg}\\.`, 'i').test(name)) {
      if (/^\d+(\.\d+)?$/.test(name)) {
        name = `${seg}.${name}`;
      } else {
        name = `${seg}.${name}`;
      }
    }
    out.push({ name, value: val });
  }
  return out;
}

/**
 * Serializes HL7 Build Path model to persisted path and display key (OBR/OBX use @parentId when needed).
 * @example serializeHl7PathFromBuildPathModel(model, 'OBX').displayKey // 'OBX.5.1'
 */
export function serializeHl7PathFromBuildPathModel(
  model: PathBuildModel,
  targetSegment: string
): { path: string; displayKey: string } {
  const target = (targetSegment || '').trim().toUpperCase();
  const targetStep = model.steps.find(
    (s) => s.isRepeatable && s.elementName.toUpperCase() === target
  );
  if (!targetStep) {
    return serializeHl7PathFromComponentSteps(model);
  }
  const leafKey = (model.leafAttribute || '').trim();
  const leafParts = leafKey ? parseHl7ComponentKeyParts(target, leafKey) : null;
  const fi =
    leafParts?.fieldIndex != null
      ? `${leafParts.fieldIndex}`
      : (targetStep.hl7FieldIndex ?? '').trim();
  const ci =
    leafParts?.componentIndex != null
      ? `${leafParts.componentIndex}`
      : (targetStep.hl7ComponentIndex ?? '').trim();
  const sci =
    leafParts?.subComponentIndex != null ? `${leafParts.subComponentIndex}` : '';
  const displayKey = fi
    ? hl7FlatDisplayKeyFromIndices(target, fi, ci || null, sci || null)
    : target;

  const obrStep = model.steps.find((s) => s.isRepeatable && s.elementName.toUpperCase() === 'OBR');
  const obxStep = model.steps.find((s) => s.isRepeatable && s.elementName.toUpperCase() === 'OBX');

  if (target === 'OBX' && obrStep && obxStep) {
    const leafTail = leafKey
      ? expandHl7ComponentKeyToSlashNotation(leafKey)
      : hl7LeafTailFromStep(targetStep);
    const path = buildHl7ObxParentIdPath({
      obrComponents: hl7FiltersToComponents(obrStep),
      obxComponents: hl7FiltersToComponents(obxStep),
      obxOccurrence: Math.max(1, Math.floor(Number(obxStep.occurrenceIndex) || 1)),
      leafTail,
    });
    return { path: normalizeXmlFieldPersistPath(path), displayKey };
  }

  if (target === 'OBR' && obrStep) {
    const leafTail = leafKey
      ? expandHl7ComponentKeyToSlashNotation(leafKey)
      : hl7LeafTailFromStep(obrStep, sci);
    const path = buildHl7ObrRepeatPath({
      obrComponents: hl7FiltersToComponents(obrStep),
      obrOccurrence: Math.max(1, Math.floor(Number(obrStep.occurrenceIndex) || 1)),
      leafTail,
    });
    return { path: normalizeXmlFieldPersistPath(path), displayKey };
  }

  return serializeHl7PathFromComponentSteps(model);
}

/**
 * Serializes path from per-repeatable-segment component/subcomponent picks (no attribute filters).
 * @example serializeHl7PathFromComponentSteps(model).path // 'HL7Message/MSH/MSH.12'
 */
export function serializeHl7PathFromComponentSteps(model: PathBuildModel): {
  path: string;
  displayKey: string;
} {
  const repeatable = model.steps.filter((s) => s.isRepeatable);
  let path = 'HL7Message';
  let displayKey = '';
  for (const step of repeatable) {
    const seg = step.elementName;
    const fi = (step.hl7FieldIndex ?? '').trim();
    const ci = (step.hl7ComponentIndex ?? '').trim();
    const base = normalizeXmlFieldPersistPath(`${path}/${seg}`);
    if (fi) {
      let sub: string | null = null;
      const leafAttr = (model.leafAttribute || '').trim();
      if (leafAttr.toUpperCase().startsWith(`${seg.toUpperCase()}.`)) {
        const leafParsed = parseHl7ComponentKeyParts(seg, leafAttr);
        if (leafParsed.subComponentIndex != null) {
          sub = `${leafParsed.subComponentIndex}`;
        }
      }
      const built = buildHl7PathFromSegmentPicker({
        segment: seg,
        fieldIndex: fi,
        componentIndex: ci || null,
        subComponentIndex: sub,
        segmentBasePath: base,
      });
      path = built.path;
      displayKey = built.displayKey;
    } else {
      path = base;
    }
  }
  if (!displayKey && (model.leafAttribute || '').trim()) {
    displayKey = model.leafAttribute.trim();
  }
  return { path: normalizeXmlFieldPersistPath(path), displayKey };
}

/**
 * Builds PathBuildModel for a repeatable HL7 field from display key and template path.
 * @example buildHl7PathModelForRepeatableField('OBX.5.1', 'HL7Message/OBR/OBX/OBX.5/OBX.5.1')?.serializeTemplate
 */
export function buildHl7PathModelForRepeatableField(
  displayKey: string,
  templatePath: string
): { model: PathBuildModel; serializeTemplate: string } | null {
  const tp = (templatePath || '').trim();
  if (!tp) {
    return null;
  }
  const exact = buildHl7PathModelFromTemplatePath(tp);
  if (exact) {
    return exact;
  }
  const m = /^([A-Za-z0-9]{2,4})/.exec((displayKey || '').trim());
  const target = m ? m[1].toUpperCase() : '';
  if (!target) {
    return null;
  }
  return buildHl7PathModelForTargetSegment(target, displayKey.trim(), tp);
}

/** Splits bracket predicate text on `and` into attribute/value filter rows. @example splitHl7PredicateConjunctions("OBR.4='1' and OBR.3='x'") */
function splitHl7PredicateConjunctions(inner: string): PathFilterRow[] {
  const s = (inner || '').trim();
  if (!s) {
    return [];
  }
  return s
    .split(/\s+and\s+/i)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      // HL7 persisted paths use slash keys (e.g. OBR.3/OBR.3.1='x', OBX.6/OBX.6.4='y').
      const quoted = /^([A-Za-z0-9][A-Za-z0-9./]*)\s*=\s*'((?:\\'|[^'])*)'$/.exec(part);
      if (quoted) {
        return {
          attribute: collapseHl7SlashPredicateAttributeToFlatKey(quoted[1]),
          value: quoted[2].replace(/\\'/g, "'"),
        };
      }
      const num = /^([A-Za-z0-9][A-Za-z0-9./]*)\s*=\s*(-?\d+(?:\.\d+)?)$/.exec(part);
      if (num) {
        return {
          attribute: collapseHl7SlashPredicateAttributeToFlatKey(num[1]),
          value: num[2],
        };
      }
      return { attribute: '', value: '' };
    })
    .filter((r) => (r.attribute || '').trim());
}

/**
 * Parses persisted OBX path that uses @parentId linking to a filtered OBR occurrence.
 * @example parseHl7ObxParentIdPersistedPath('HL7Message/OBX[@parentId=...][1]/OBX.5/OBX.5.1')?.leaf
 */
export function parseHl7ObxParentIdPersistedPath(persisted: string): {
  obrFilters: PathFilterRow[];
  obxFilters: PathFilterRow[];
  obxOccurrence: number;
  leaf: string;
} | null {
  let norm = normalizeXmlFieldPersistPath(persisted);
  norm = norm.replace(/\[@parentId\s*=\s*/i, '[@parentId=');
  const key = '[@parentId=';
  const idx = norm.indexOf(key);
  if (idx < 0 || !norm.startsWith('HL7Message/OBX')) {
    return null;
  }
  let rest = norm.slice(idx + key.length);
  const idMarker = '/@id';
  const idPos = rest.indexOf(idMarker);
  if (idPos < 0) {
    return null;
  }
  const obrPathSlice = rest.slice(0, idPos);
  rest = rest.slice(idPos + idMarker.length);
  const obrM = obrPathSlice.match(/^\/HL7Message\/OBR\[(.*)\]$/);
  const obrInner = obrM ? obrM[1] : '';
  const obrFilters = splitHl7PredicateConjunctions(obrInner);
  let obxFilters: PathFilterRow[] = [];
  let obxOccurrence = 1;
  let tail = '';
  if (rest.startsWith(' and ')) {
    rest = rest.slice(' and '.length);
    const rb = rest.indexOf('][');
    if (rb < 0) {
      return null;
    }
    const obxInner = rest.slice(0, rb);
    obxFilters = splitHl7PredicateConjunctions(obxInner);
    rest = rest.slice(rb + 2);
    const occM = /^(\d+)\]\//.exec(rest);
    if (!occM) {
      return null;
    }
    obxOccurrence = Math.max(1, parseInt(occM[1], 10) || 1);
    tail = rest.slice(occM[0].length);
  } else if (rest.startsWith('][')) {
    rest = rest.slice(2);
    const occM = /^(\d+)\]\//.exec(rest);
    if (!occM) {
      return null;
    }
    obxOccurrence = Math.max(1, parseInt(occM[1], 10) || 1);
    tail = rest.slice(occM[0].length);
  } else {
    return null;
  }
  return { obrFilters, obxFilters, obxOccurrence, leaf: tail };
}

/**
 * Derives flat display key from persisted HL7 path (e.g. OBX.2.2), including @parentId and OBR repeat paths.
 * @example hl7DisplayKeyFromPersistedPath('HL7Message/MSH/MSH.12') // 'MSH.12'
 */
export function hl7DisplayKeyFromPersistedPath(persisted: string): string | null {
  const raw = (persisted || '').toString().trim();
  if (!raw) {
    return null;
  }
  if (/^[A-Za-z0-9]{2,4}\.\d+(?:\.\d+)*$/.test(raw)) {
    return raw;
  }
  if (!/^hl7message\//i.test(raw) && !raw.includes('[@parentId')) {
    return null;
  }
  const norm = normalizeXmlFieldPersistPath(raw);

  const parsedParent = parseHl7ObxParentIdPersistedPath(norm);
  if (parsedParent?.leaf) {
    const tail = parsedParent.leaf.includes('/')
      ? parsedParent.leaf.split('/').filter(Boolean).pop()!
      : parsedParent.leaf;
    const flat = collapseHl7SlashPredicateAttributeToFlatKey(tail);
    if (/^[A-Za-z0-9]{2,4}\.\d+/.test(flat)) {
      return flat;
    }
  }

  const parsedObr = parseHl7ObrRepeatPersistedPath(norm);
  if (parsedObr?.leaf) {
    const tail = parsedObr.leaf.includes('/')
      ? parsedObr.leaf.split('/').filter(Boolean).pop()!
      : parsedObr.leaf;
    const flat = collapseHl7SlashPredicateAttributeToFlatKey(tail);
    if (/^[A-Za-z0-9]{2,4}\.\d+/.test(flat)) {
      return flat;
    }
  }

  const occTail = /\]\[\d+\]\/(.+)$/i.exec(norm);
  if (occTail?.[1]) {
    const last = occTail[1].split('/').filter(Boolean).pop() || '';
    const flat = collapseHl7SlashPredicateAttributeToFlatKey(last);
    if (/^[A-Za-z0-9]{2,4}\.\d+/.test(flat)) {
      return flat;
    }
  }

  const lastSeg = norm.split('/').pop() || '';
  if (lastSeg && !lastSeg.includes('[') && !lastSeg.includes('@')) {
    const flat = collapseHl7SlashPredicateAttributeToFlatKey(lastSeg);
    if (/^[A-Za-z0-9]{2,4}\.\d+/.test(flat)) {
      return flat;
    }
  }
  return null;
}

/**
 * Parses persisted OBR repeat path with optional predicates and occurrence index.
 * @example parseHl7ObrRepeatPersistedPath('HL7Message/OBR[OBR.4=1][2]/OBR.11/OBR.11.1')?.obrOccurrence
 */
export function parseHl7ObrRepeatPersistedPath(persisted: string): {
  obrFilters: PathFilterRow[];
  obrOccurrence: number;
  leaf: string;
} | null {
  const norm = normalizeXmlFieldPersistPath(persisted);
  if (!norm.startsWith('HL7Message/OBR') || norm.includes('[@parentId')) {
    return null;
  }
  const withPred = /^HL7Message\/OBR\[([^\]]+)\]\[(\d+)\]\/(.+)$/.exec(norm);
  if (withPred) {
    return {
      obrFilters: splitHl7PredicateConjunctions(withPred[1]),
      obrOccurrence: Math.max(1, parseInt(withPred[2], 10) || 1),
      leaf: withPred[3],
    };
  }
  const occOnly = /^HL7Message\/OBR\[(\d+)\]\/(.+)$/.exec(norm);
  if (occOnly) {
    return {
      obrFilters: [],
      obrOccurrence: Math.max(1, parseInt(occOnly[1], 10) || 1),
      leaf: occOnly[2],
    };
  }
  const plain = /^HL7Message\/OBR\/(.+)$/.exec(norm);
  if (plain) {
    return { obrFilters: [], obrOccurrence: 1, leaf: plain[1] };
  }
  return null;
}

/** Hydrates OBR/OBX filters and leaf from an @parentId persisted OBX path. @example tryApplyHl7ObxParentIdPersisted(model, 'HL7Message/OBX[@parentId=...]') */
function tryApplyHl7ObxParentIdPersisted(model: PathBuildModel, persistedPath: string): boolean {
  const obxStep = model.steps.find((s) => s.elementName.toUpperCase() === 'OBX');
  if (!obxStep) {
    return false;
  }
  const parsed = parseHl7ObxParentIdPersistedPath(persistedPath);
  if (!parsed) {
    return false;
  }
  const obrStep = model.steps.find((s) => s.elementName.toUpperCase() === 'OBR');
  if (obrStep) {
    obrStep.filters = parsed.obrFilters.length ? [...parsed.obrFilters] : [];
    obrStep.hl7FieldIndex = '';
    obrStep.hl7ComponentIndex = '';
  }
  obxStep.filters = parsed.obxFilters.length ? [...parsed.obxFilters] : [];
  obxStep.occurrenceIndex = parsed.obxOccurrence;
  applyHl7LeafTailToStep(obxStep, parsed.leaf);
  const leafFlat = deepestHl7FlatKeyForSegment(
    'OBX',
    parsed.leaf.split('/').filter(Boolean),
    0
  );
  if (leafFlat) {
    model.leafAttribute = leafFlat;
  } else {
    const fi = (obxStep.hl7FieldIndex ?? '').trim();
    model.leafAttribute = fi
      ? hl7FlatDisplayKeyFromIndices('OBX', fi, obxStep.hl7ComponentIndex ?? '')
      : parsed.leaf.replace(/^@+/, '').trim();
  }
  return true;
}

/** Hydrates OBR filters, occurrence, and leaf from a repeat-style persisted OBR path. @example tryApplyHl7ObrRepeatPersisted(model, 'HL7Message/OBR[OBR.4=1][2]/OBR.11/OBR.11.1') */
function tryApplyHl7ObrRepeatPersisted(model: PathBuildModel, persistedPath: string): boolean {
  const obrStep = model.steps.find((s) => s.elementName.toUpperCase() === 'OBR');
  if (!obrStep) {
    return false;
  }
  const parsed = parseHl7ObrRepeatPersistedPath(persistedPath);
  if (!parsed) {
    return false;
  }
  obrStep.filters = parsed.obrFilters.length ? [...parsed.obrFilters] : [];
  obrStep.occurrenceIndex = parsed.obrOccurrence;
  applyHl7LeafTailToStep(obrStep, parsed.leaf);
  const leafFlat = deepestHl7FlatKeyForSegment(
    'OBR',
    parsed.leaf.split('/').filter(Boolean),
    0
  );
  if (leafFlat) {
    model.leafAttribute = leafFlat;
  } else {
    const fi = (obrStep.hl7FieldIndex ?? '').trim();
    model.leafAttribute = fi
      ? hl7FlatDisplayKeyFromIndices('OBR', fi, obrStep.hl7ComponentIndex ?? '')
      : parsed.leaf.replace(/^@+/, '').trim();
  }
  return true;
}

/**
 * Hydrates a PathBuildModel from a persisted HL7 path (OBX @parentId, OBR repeat, or component path).
 * @example applyHl7PersistedPathToModel(model, savedPath, templatePath); model.leafAttribute
 */
export function applyHl7PersistedPathToModel(
  model: PathBuildModel,
  persistedPath: string,
  serializeTemplate: string
): void {
  const pNorm = normalizeXmlFieldPersistPath(persistedPath);
  if (tryApplyHl7ObxParentIdPersisted(model, pNorm)) {
    return;
  }
  if (tryApplyHl7ObrRepeatPersisted(model, pNorm)) {
    return;
  }
  if (pNorm.startsWith('HL7Message/') && !pNorm.includes('[@')) {
    applyHl7ComponentPathToModel(model, pNorm);
    return;
  }
  const tNorm = normalizeXmlFieldPersistPath(serializeTemplate);
  const pParts = pNorm.split('/').filter(Boolean);
  const tParts = tNorm.split('/').filter(Boolean);
  if (pParts.length !== tParts.length || !pParts.length) {
    return;
  }
  const leaf = pParts[pParts.length - 1];
  const tLeaf = tParts[tParts.length - 1];
  if (bareSegmentName(leaf) !== bareSegmentName(tLeaf)) {
    return;
  }
  model.leafAttribute = leaf.replace(/^@+/, '').trim();

  let si = 0;
  for (let i = 0; i < pParts.length - 1; i++) {
    const pSeg = pParts[i];
    const tBare = bareSegmentName(tParts[i]);
    const step = model.steps[si];
    if (step && step.elementName === tBare) {
      const parsed = parsePredicateElementSegment(pSeg);
      if (parsed.name !== step.elementName) {
        return;
      }
      step.filters =
        parsed.predicates.filter(
          (r) => (r.attribute ?? '').trim() && `${r.value ?? ''}`.trim() !== ''
        ) ?? [];
      if (parsed.occurrence != null && Number.isFinite(parsed.occurrence) && parsed.occurrence >= 1) {
        step.occurrenceIndex = parsed.occurrence;
      }
      si++;
    }
  }
}

/**
 * Serializes PathBuildModel to persisted path using HL7 template and filter/component picks.
 * @example serializeHl7PathModelToPath(model, 'HL7Message/OBR/OBX/OBX.5/OBX.5.1')
 */
export function serializeHl7PathModelToPath(model: PathBuildModel, serializeTemplate: string): string {
  const hasComponentPicks = model.steps.some(
    (s) => s.isRepeatable && (s.hl7FieldIndex ?? '').trim()
  );
  if (hasComponentPicks) {
    const target = ((model.leafAttribute || '').trim().split('.')[0] || '').toUpperCase();
    return serializeHl7PathFromBuildPathModel(model, target).path;
  }
  const norm = normalizeXmlFieldPersistPath(serializeTemplate);
  if (norm.includes('/OBR/OBX/')) {
    const obrStep = model.steps.find((s) => s.elementName === 'OBR');
    const obxStep = model.steps.find((s) => s.elementName === 'OBX');
    if (obrStep && obxStep) {
      const toComponents = (step: PathBuildElementStep) =>
        (step.filters ?? [])
          .filter((f) => (f.attribute ?? '').trim() && `${f.value ?? ''}`.trim() !== '')
          .map((f) => ({ name: f.attribute.trim(), value: `${f.value}`.trim() }));
      return buildHl7ObxParentIdPath({
        obrComponents: toComponents(obrStep),
        obxComponents: toComponents(obxStep),
        obxOccurrence: Math.max(1, Math.floor(Number(obxStep.occurrenceIndex) || 1)),
        leafTail: (model.leafAttribute || '').trim().replace(/^@+/, ''),
      });
    }
  }
  const parts = norm.split('/').filter(Boolean);
  if (parts.length < 2) {
    return norm;
  }
  const templateLeaf = parts[parts.length - 1];
  const modelLeaf = (model.leafAttribute || '').trim().replace(/^@+/, '');
  if (bareSegmentName(templateLeaf) !== bareSegmentName(modelLeaf)) {
    return norm;
  }
  const prefix = parts.slice(0, -1);
  let si = 0;
  const out: string[] = [];
  for (const rawSeg of prefix) {
    const bare = bareSegmentName(rawSeg);
    const step = model.steps[si];
    if (step && step.elementName === bare) {
      let token = bare;
      const nonemptyFilters = (step.filters ?? []).filter(
        (f) => (f.attribute ?? '').trim() && `${f.value ?? ''}`.trim() !== ''
      );
      for (const f of nonemptyFilters) {
        const opt = step.attributeOptions.find((o) => o.name === f.attribute.trim());
        token += formatXmlAttributePredicate(
          f.attribute.trim(),
          `${f.value}`.trim(),
          coalesceDataType(opt, f.value)
        );
      }
      if (step.isRepeatable) {
        const n = Math.max(1, Math.floor(Number(step.occurrenceIndex) || 1));
        token += `[${n}]`;
      }
      out.push(token);
      si++;
    } else {
      out.push(rawSeg);
    }
  }
  out.push(modelLeaf);
  return normalizeXmlFieldPersistPath(out.join('/'));
}
